import { DropDownList, DropDownListChangeEvent, DropDownListFilterChangeEvent, DropDownListPageChangeEvent } from '@progress/kendo-react-dropdowns'
import { loadMessages, LocalizationProvider } from '@progress/kendo-react-intl'
import CustomText from 'components/text/CustomText'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { FixMeLater, IDropdownOption, TDropdown } from 'types'
import trMessages from './shared-dd-tr'
import enMessages from './shared-dd-en'
import arMessages from './shared-dd-ar'
import { useAppSelector } from 'app/hooks'
loadMessages(trMessages, 'tr')
loadMessages(enMessages, 'en')
loadMessages(arMessages, 'ar')

interface IDropdownProps {
  selectedOption: IDropdownOption
  options?: TDropdown
  onChangeOption: (params: IDropdownOption) => void
  className?: string
  title?: string
  disabled?: boolean
  defaultItem?: { title: string }
  style?: React.CSSProperties
  requestData: (skip: number, filter: string) => Promise<{ data: TDropdown; total: number }>
  setFilterDataState: (filter: FixMeLater) => void
  filterDataState?: FixMeLater
  setDropdownData: (data: TDropdown) => void
}

const emptyItem: IDropdownOption = { title: 'loading ...', value: null };
const pageSize = 10

const loadingData: TDropdown = [];
while (loadingData.length < pageSize) {
    loadingData.push(emptyItem);
}


const DropdownListWithSearch = ({
  selectedOption,
  options = [],
  onChangeOption,
  requestData,
  title,
  className,
  disabled,
  // defaultItem,
  style,
  setFilterDataState,
  filterDataState,
  setDropdownData
}: IDropdownProps) => {


  const [visible, setVisible] = useState(false)
  const dataCaching = useRef<IDropdownOption[]>(options as IDropdownOption[]);

  const [total, setTotal] = useState(0);

  const skipRef = useRef(0);

  const resetCach = () => {
      dataCaching.current.length = 0;
  };

  const notificationLanguage =
    useAppSelector((state) => state?.auth?.user?.notificationLanguage) || 'tr'

  const ontoggleOption = () => {
    if (!disabled) {
      setVisible((prevState) => !prevState)
    }
  }

useEffect(() => {
    const loadInitialData = async () => {
        try {
            const result = await requestData(0, filterDataState?.filter?.value || '');
            if (result) {
                dataCaching.current = result.data;
                setTotal(result.total);
                setDropdownData(result.data.slice(0, pageSize));
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
        }
    };
    
    loadInitialData();
    
    return () => {
        resetCach();
    };
}, [filterDataState, requestData]);

const onFilterChange = useCallback(
  async (event: DropDownListFilterChangeEvent) => {
    const filter = event.filter.value

    resetCach()
    setDropdownData(loadingData)
    skipRef.current = 0
    setFilterDataState({ filter: event.filter })
    
    try {
      const result = await requestData(0, filter)
      if (result) {
        dataCaching.current = result.data;
        setTotal(result.total);
        setDropdownData(result.data.slice(0, pageSize));
      }
    } catch (error) {
      console.error('Error filtering data:', error);
    }
  },
  [requestData],
)

const shouldRequestData = useCallback((skip: number) => {
    for (let i = 0; i < pageSize; i++) {
        if (!dataCaching.current[skip + i]) {
            return true;
        }
    }
    return false;
}, []);

const getCachedData = useCallback((skip: number) => {
    const data: IDropdownOption[] = [];
    for (let i = 0; i < pageSize; i++) {
        data.push(dataCaching.current[i + skip] || { ...emptyItem });
    }
    return data;
}, []);


  const onChange = useCallback((event: DropDownListChangeEvent) => {
    const value = event.target.value;
    if (value && value.title === emptyItem.title) {
        return;
    }
    onChangeOption(value as IDropdownOption);
}, []);

const pageChange = useCallback(
  async (event: DropDownListPageChangeEvent) => {
      const newSkip = event.page.skip;

      if (shouldRequestData(newSkip)) {
          try {
              const result = await requestData(newSkip, filterDataState?.filter?.value || '');
              if (result) {
                  // Cache'e yeni verileri ekle
                  for (let i = 0; i < result.data.length; i++) {
                      dataCaching.current[newSkip + i] = result.data[i];
                  }
                  setTotal(result.total);
              }
          } catch (error) {
              console.error('Error loading page data:', error);
          }
      }

      const data = getCachedData(newSkip);
      setDropdownData(data as TDropdown);
      skipRef.current = newSkip;
  },
  [filterDataState, getCachedData, requestData, shouldRequestData]
);

  return (
    <>
      <LocalizationProvider
        language={`${notificationLanguage}-${notificationLanguage?.toUpperCase()}`}
      >
        <DrowdownContainer className={className} onClick={ontoggleOption}>
          {title && (
            <TitleWrapper>
              <CustomText typography="header3">{title}</CustomText>
            </TitleWrapper>
          )}
          <DropDownList
                data={options}
                value={selectedOption}
                onChange={onChange}
                dataItemKey="value"
                textField="title"
                filterable={true}
                onFilterChange={onFilterChange}
                virtual={{
                    pageSize: pageSize,
                    skip: skipRef.current,
                    total: total
                }}
                onPageChange={pageChange}
                style={style}
          />
        </DrowdownContainer>
        {visible && <BackDrop />}
      </LocalizationProvider>
    </>
  )
}

export default DropdownListWithSearch

const BackDrop = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;
  top: 0;
  z-index: 1;
  pointer-events: none;
`

const DrowdownContainer = styled.div`
  margin-top: 5px;
  gap: 3px;
  position: relative;
  z-index: 4;
`

const TitleWrapper = styled.div`
  gap: 3px;
  display: flex;
  align-items: flex-end;
  margin-bottom: 8px;
`
